<template>
  <div class="relative h-screen overflow-hidden" @contextmenu.prevent="onDesktopContextMenu">
    <!-- Static Bliss wallpaper -->
    <img
      src="/assets/imgs/wallpaper/bliss.jpg"
      alt=""
      aria-hidden="true"
      class="absolute inset-0 block h-full w-full object-cover"
      draggable="false"
    />

    <!-- Desktop right-click menu -->
    <div
      v-if="ctxMenu"
      class="xp-context-menu xp-font fixed z-[70] w-44 border border-[#0831d9] bg-[var(--xp-face)] py-1 shadow-[3px_3px_12px_rgba(0,0,0,0.5)]"
      :style="{ left: `${ctxMenu.x}px`, top: `${ctxMenu.y}px` }"
      role="menu"
    >
      <button
        class="xp-menubar-item flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-black"
        role="menuitem"
        @click="openDisplay"
      >
        <span aria-hidden="true">🖥️</span> {{ t('xp.context.properties') }}
      </button>
    </div>

    <!-- Display Properties dialog -->
    <div
      v-if="displayOpen"
      class="fixed inset-0 bottom-[30px] z-[60] grid place-items-center px-3"
    >
      <XpDisplayProperties @apply="applyCrt" @close="displayOpen = false" />
    </div>

    <!-- Desktop icons -->
    <div class="absolute left-3 top-3 z-10 flex flex-col gap-1">
      <XpDesktopIcon
        v-for="id in WINDOW_IDS"
        :key="id"
        :glyph="WINDOW_META[id].glyph"
        :label="taskLabel(id)"
        @open="openWindow(id)"
      />
    </div>

    <!-- Open windows (all start closed) -->
    <div class="absolute inset-0 bottom-[30px] grid place-items-center px-3">
      <div
        v-for="(id, pos) in openOrder"
        :key="id"
        class="col-start-1 row-start-1 w-full"
        :style="{
          maxWidth: '940px',
          zIndex: maximizedId === id ? 50 : 10 + pos,
          transform: maximizedId === id ? 'none' : `translate(${pos * 22}px, ${pos * -14}px)`,
          display: minimized.has(id) ? 'none' : undefined,
        }"
      >
        <XpWindow
          :title="windowTitle(id)"
          :icon="WINDOW_META[id].glyph"
          :active="focusedId === id"
          :minimized="false"
          :maximized="maximizedId === id"
          :width="sizes[id].w"
          :body-height="sizes[id].h"
          @minimize="minimize(id)"
          @maximize="toggleMaximize(id)"
          @close="closeWindow(id)"
          @focus="focusWindow(id)"
          @resize="onResize(id, $event)"
        >
          <div class="pr-1" @mousedown="focusWindow(id)">
            <ExperienceExplorer
              v-if="id === 'experience'"
              @navigate="explorerTitle = $event"
            />
            <component :is="sections[id]" v-else />
          </div>
        </XpWindow>
      </div>
    </div>

    <!-- Taskbar: only open windows -->
    <XpTaskbar
      class="absolute inset-x-0 bottom-0 z-40"
      :items="openItems"
      :active-id="focusedId ?? ''"
      :user-name="t('hero.name')"
      :start-label="t('xp.start')"
      :logoff-label="t('xp.logoff')"
      :turn-off-label="t('xp.startmenu.turnOff')"
      :all-programs-label="t('xp.startmenu.allPrograms')"
      :help-label="t('xp.startmenu.help')"
      :locales="locales"
      @navigate="taskbarActivate($event as WindowId)"
      @locale="$emit('locale', $event)"
      @logoff="$emit('logoff')"
      @power="$emit('power')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import XpWindow from './XpWindow.vue'
import XpDesktopIcon from './XpDesktopIcon.vue'
import XpTaskbar from './XpTaskbar.vue'
import XpDisplayProperties from './XpDisplayProperties.vue'
import ExperienceExplorer from './ExperienceExplorer.vue'
import ProfileSection from '../sections/ProfileSection.vue'
import MetricsSection from '../sections/MetricsSection.vue'
import ProjectsSection from '../sections/ProjectsSection.vue'
import StackSection from '../sections/StackSection.vue'
import ContactSection from '../sections/ContactSection.vue'
import { WINDOW_IDS, WINDOW_META, type WindowId } from './windows'
import { setCrt } from '../composables/useCrt'
import type { Locale } from '../locales'

defineEmits<{
  logoff: []
  power: []
  locale: [loc: Locale]
}>()

const { t } = useI18n()

const sections = {
  profile: ProfileSection,
  metrics: MetricsSection,
  projects: ProjectsSection,
  stack: StackSection,
  contact: ContactSection,
} as const

const locales: Locale[] = ['pt-BR', 'en']

/** Open windows in z-order (last = front). Starts empty: all closed. */
const openOrder = ref<WindowId[]>([])
const minimized = ref<Set<WindowId>>(new Set())
const focusedId = ref<WindowId | null>(null)
const maximizedId = ref<WindowId | null>(null)
/** Current explorer location label (empty = root). */
const explorerTitle = ref('')
/** Desktop right-click menu position (null = closed). */
const ctxMenu = ref<{ x: number; y: number } | null>(null)
/** Display Properties dialog visibility. */
const displayOpen = ref(false)

function onDesktopContextMenu(e: MouseEvent): void {
  const target = e.target as HTMLElement
  if (target.closest('.xp-window, .xp-taskbar, .xp-desktop-icon, .xp-context-menu')) return
  ctxMenu.value = {
    x: Math.min(e.clientX, window.innerWidth - 190),
    y: Math.min(e.clientY, window.innerHeight - 120),
  }
}

function closeCtxMenu(): void {
  ctxMenu.value = null
}

function onGlobalPointerDown(e: PointerEvent): void {
  if ((e.target as HTMLElement).closest('.xp-context-menu')) return
  closeCtxMenu()
}

function onCtxKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') closeCtxMenu()
}

function openDisplay(): void {
  closeCtxMenu()
  displayOpen.value = true
}

function applyCrt(on: boolean): void {
  setCrt(on)
}

if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', onGlobalPointerDown)
  window.addEventListener('keydown', onCtxKey)
}

onBeforeUnmount(() => {
  window.removeEventListener('pointerdown', onGlobalPointerDown)
  window.removeEventListener('keydown', onCtxKey)
})

/** Per-window geometry. Default is a standard square-ish 620px window. */
const DEFAULT_SIZE = { w: 620, h: 580 }
const sizes = ref<Record<WindowId, { w: number; h: number | null }>>({
  profile: { ...DEFAULT_SIZE },
  metrics: { ...DEFAULT_SIZE },
  experience: { ...DEFAULT_SIZE },
  projects: { ...DEFAULT_SIZE },
  stack: { ...DEFAULT_SIZE },
  contact: { ...DEFAULT_SIZE },
})

function onResize(id: WindowId, size: { w: number; h: number | null }): void {
  sizes.value[id] = { ...size }
}

function taskLabel(id: WindowId): string {
  return t(`xp.desktop.${id}`)
}

function windowTitle(id: WindowId): string {
  if (id === 'experience' && explorerTitle.value) return explorerTitle.value
  return t(`xp.windows.${id}.title`)
}

const openItems = computed(() =>
  openOrder.value.map((id) => ({ id, label: taskLabel(id), glyph: WINDOW_META[id].glyph })),
)

function openWindow(id: WindowId): void {
  if (id === 'experience') explorerTitle.value = ''
  if (!openOrder.value.includes(id)) openOrder.value.push(id)
  const rest = minimized.value
  if (rest.has(id)) {
    const next = new Set(rest)
    next.delete(id)
    minimized.value = next
  }
  focusWindow(id)
}

function focusWindow(id: WindowId): void {
  openOrder.value = [...openOrder.value.filter((w) => w !== id), id]
  focusedId.value = id
}

function toggleMaximize(id: WindowId): void {
  maximizedId.value = maximizedId.value === id ? null : id
  focusWindow(id)
}

function minimize(id: WindowId): void {
  const next = new Set(minimized.value)
  next.add(id)
  minimized.value = next
  if (maximizedId.value === id) maximizedId.value = null
  // Focus the topmost visible window, if any.
  const visible = [...openOrder.value].reverse().find((w) => !next.has(w))
  focusedId.value = visible ?? null
}

function closeWindow(id: WindowId): void {
  openOrder.value = openOrder.value.filter((w) => w !== id)
  if (maximizedId.value === id) maximizedId.value = null
  const rest = minimized.value
  if (rest.has(id)) {
    const next = new Set(rest)
    next.delete(id)
    minimized.value = next
  }
  if (focusedId.value === id) {
    const visible = [...openOrder.value].reverse().find((w) => !minimized.value.has(w))
    focusedId.value = visible ?? null
  }
}

function taskbarActivate(id: WindowId): void {
  if (!openOrder.value.includes(id)) {
    openWindow(id)
    return
  }
  if (minimized.value.has(id)) {
    openWindow(id)
    return
  }
  if (focusedId.value === id) {
    minimize(id)
  } else {
    focusWindow(id)
  }
}
</script>
